import { ErrorHandler, Injectable } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { getSentry } from 'src/app/core/helpers/sentry-plugin-wrapper';
import { environment } from 'src/environments/environment';
import { getSentryEnabled, logErrorInSentry } from '../modules/errors/response-error';
import { EInstance } from '../shared/enums/instances';
import { SessionService } from './session.service';

enum ESentryEnvironment {
  ASM = 'asm',
  STAGING = 'staging',
  LIVE = 'live',
  LOCAL = 'local',
  DEVELOPMENT = 'development',
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  sentry = getSentry();
  dsn =
    'https://2b2019cd3ad3348ee9b99f8b3998d500@o4508835299328001.ingest.de.sentry.io/4509480590114897';
  ua = window.navigator.userAgent;
  isIE = /MSIE|Trident/.test(this.ua);

  constructor(private sessionService: SessionService) {
    this.initSentry();
  }

  handleError(error): void {
    const unwrappedError = error.originalError || error;

    logErrorInSentry({
      error: unwrappedError,
      message: unwrappedError.message || error.statusText || 'Unhandled error in Angular app',
      status: error.status,
    });
    console.error(unwrappedError);

    throw error;
  }

  private initSentry(): void {
    if (this.isIE) {
      return;
    }

    const url = window.location.host;

    const sentryEnvironment: string = this.getSentryEnvironment(url);

    this.sentry.init({
      enabled: getSentryEnabled(),
      dsn: this.dsn,
      tracePropagationTargets: [
        EInstance.DEVELOPMENT,
        EInstance.STAGING,
        EInstance.LIVE,
        EInstance.ASM,
        EInstance.LOCALHOST,
        /^\//,
      ],
      integrations: [
        this.sentry.browserTracingIntegration(),
        this.sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
        }),
      ],
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: sentryEnvironment,
      attachStacktrace: true,
      normalizeDepth: 5,
      ignoreErrors: [
        `${EStatusCode.UNAUTHORIZED}`,
        `${EStatusCode.FORBIDDEN}`,
        `${EStatusCode.UPGRADE_REQUIRED}`,
      ],
      beforeSend(event, hint) {
        const error = hint?.originalException;

        if (
          error instanceof HttpErrorResponse &&
          (error.status === EStatusCode.UNAUTHORIZED ||
            error.status === EStatusCode.FORBIDDEN ||
            error.status === EStatusCode.UPGRADE_REQUIRED)
        ) {
          return null;
        }

        const eventErrorStatus = (event?.exception?.values?.[0]?.value as any)?.status;
        const errorStatus = (event?.message as any)?.status;

        const isUnauthorized =
          event.tags?.error_status === EStatusCode.UNAUTHORIZED ||
          errorStatus === EStatusCode.UNAUTHORIZED ||
          eventErrorStatus === EStatusCode.UNAUTHORIZED;

        const isForbidden =
          event.tags?.error_status === EStatusCode.FORBIDDEN ||
          errorStatus === EStatusCode.FORBIDDEN ||
          eventErrorStatus === EStatusCode.FORBIDDEN;

        const isUpgradeRequired =
          event.tags?.error_status === EStatusCode.UPGRADE_REQUIRED ||
          errorStatus === EStatusCode.UPGRADE_REQUIRED ||
          eventErrorStatus === EStatusCode.UPGRADE_REQUIRED;

        if (isUnauthorized || isForbidden || isUpgradeRequired) {
          return null;
        } else {
          return event;
        }
      },
    });

    this.sentry.setTag('session_id', this.sessionService.getSessionId());
  }

  private getSentryEnvironment(url: string): string {
    let sentryEnvironment: string;

    if (url === EInstance.ASM) {
      sentryEnvironment = ESentryEnvironment.ASM;
    } else if (environment.production) {
      sentryEnvironment = ESentryEnvironment.LIVE;
    } else if (environment.staging) {
      sentryEnvironment = ESentryEnvironment.STAGING;
    } else if (environment.development) {
      sentryEnvironment = ESentryEnvironment.DEVELOPMENT;
    } else {
      sentryEnvironment = ESentryEnvironment.LOCAL;
    }
    return sentryEnvironment;
  }
}
